import { FR_EN, SKIP_PATTERNS } from '@/lib/translations';
import GENERATED from '@/lib/fr_en_generated.json';

const DICT = new Map();
const TEMPLATES = [];
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const norm = (s) => s.replace(/\s+/g, ' ').trim();

Object.entries({ ...GENERATED, ...FR_EN }).forEach(([key, en]) => {
  let fr = key;
  if (!en || en === fr) return;
  if (fr.includes('{x}')) {
    fr = norm(fr);
    const statics = fr.split('{x}').filter(Boolean);
    if (!statics.length) return;
    const longest = statics.reduce((a, b) => (b.length > a.length ? b : a));
    const re = new RegExp('^' + fr.split('{x}').map((p) => escapeRe(p)).join('([\\s\\S]+?)') + '$');
    TEMPLATES.push({ re, en, longest: longest.trim() });
  } else {
    DICT.set(norm(fr), en);
  }
});
TEMPLATES.sort((a, b) => b.longest.length - a.longest.length);

const PUNCT = /^([\s·•*:\-–—→←(]*)(.*?)([\s·•*:.…!?)\-–—→]*)$/s;

function lookup(core) {
  if (DICT.has(core)) return DICT.get(core);
  const lower = core.charAt(0).toLowerCase() + core.slice(1);
  if (DICT.has(lower)) { const v = DICT.get(lower); return v.charAt(0).toUpperCase() + v.slice(1); }
  return null;
}

const cache = new Map();
export function translateText(raw) {
  if (!raw || !/[A-Za-zÀ-ÿ]{2}/.test(raw)) return null;
  if (cache.has(raw)) return cache.get(raw);
  let out = null;
  const text = norm(raw);
  if (!SKIP_PATTERNS.some((p) => p.test(text))) {
    out = lookup(text);
    if (!out) {
      const [, pre, core, post] = text.match(PUNCT);
      const hit = core && lookup(core);
      if (hit) out = pre + hit + post;
    }
    if (!out) {
      for (const t of TEMPLATES) {
        if (!text.includes(t.longest)) continue;
        const m = text.match(t.re);
        if (m) { let i = 1; out = t.en.replace(/\{x\}/g, () => m[i++] ?? ''); break; }
      }
    }
    if (out) {
      const lead = raw.match(/^\s*/)[0];
      const trail = raw.match(/\s*$/)[0];
      out = lead + out + trail;
    }
  }
  if (!out && window.__trMisses) window.__trMisses.add(text);
  if (cache.size > 20000) cache.clear();
  cache.set(raw, out);
  return out;
}

const ATTRS = ['placeholder', 'title', 'aria-label'];
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE', 'INPUT']);
const originals = new WeakMap();
const attrOriginals = new WeakMap();

function skippable(el) {
  return !el || el.closest?.('[data-no-translate], [contenteditable="true"]') || SKIP_TAGS.has(el.tagName);
}

function translateTextNode(node) {
  const parent = node.parentElement;
  if (skippable(parent)) return;
  const cur = node.nodeValue;
  const en = translateText(cur);
  if (en && en !== cur) {
    originals.set(node, cur);
    node.nodeValue = en;
  }
}

function translateAttrs(el) {
  if (el.closest?.('[data-no-translate]')) return;
  ATTRS.forEach((a) => {
    const v = el.getAttribute(a);
    if (!v) return;
    const en = translateText(v);
    if (en && en !== v) {
      const store = attrOriginals.get(el) || {};
      store[a] = v;
      attrOriginals.set(el, store);
      el.setAttribute(a, en);
    }
  });
}

function walk(root) {
  if (root.nodeType === Node.TEXT_NODE) return translateTextNode(root);
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  if (root.tagName === 'INPUT' || root.tagName === 'TEXTAREA') return translateAttrs(root);
  if (SKIP_TAGS.has(root.tagName)) return;
  translateAttrs(root);
  root.querySelectorAll('[placeholder],[title],[aria-label]').forEach(translateAttrs);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (tw.nextNode()) nodes.push(tw.currentNode);
  nodes.forEach(translateTextNode);
}

function restore(root) {
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (tw.nextNode()) {
    const n = tw.currentNode;
    const o = originals.get(n);
    if (o !== undefined && n.nodeValue === translateText(o)) n.nodeValue = o;
    originals.delete(n);
  }
  root.querySelectorAll('[placeholder],[title],[aria-label]').forEach((el) => {
    const store = attrOriginals.get(el);
    if (store) Object.entries(store).forEach(([a, v]) => el.setAttribute(a, v));
    attrOriginals.delete(el);
  });
}

let observer = null;
let nativeConfirm = null;
let nativeAlert = null;
let nativePrompt = null;

export function startDomTranslator() {
  if (observer) return;
  walk(document.body);
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'characterData') translateTextNode(m.target);
      else if (m.type === 'attributes') translateAttrs(m.target);
      else m.addedNodes.forEach(walk);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  nativeConfirm = window.confirm; nativeAlert = window.alert; nativePrompt = window.prompt;
  const tr = (s) => (typeof s === 'string' ? translateMultiline(s) : s);
  window.confirm = (msg) => nativeConfirm.call(window, tr(msg));
  window.alert = (msg) => nativeAlert.call(window, tr(msg));
  window.prompt = (msg, def) => nativePrompt.call(window, tr(msg), def);
}

function translateMultiline(s) {
  const whole = translateText(s);
  if (whole) return whole;
  return s.split('\n').map((line) => translateText(line) || line).join('\n');
}

export function stopDomTranslator() {
  if (!observer) return;
  observer.disconnect();
  observer = null;
  restore(document.body);
  window.confirm = nativeConfirm; window.alert = nativeAlert; window.prompt = nativePrompt;
}
